import { sharedFeedbackValue, isSharedRemoteFeedback, sharedFeedbackText, type SharedRemoteFeedback } from "./sharedFeedbackPresentation";
import { getI18n, useTranslation } from "react-i18next";
import type { ReactNode } from "react";
import source from "./locales/en/sslManagement";

export type SslKey = keyof typeof source;
export type SslValues = Record<string, string | number>;
export type SslFeedback = string | SharedRemoteFeedback | { key: SslKey; values?: SslValues };
export function useSslManagementLanguage(): void { useTranslation("sslManagement"); }
export function sslLocale(): string { return getI18n()?.resolvedLanguage ?? getI18n()?.language ?? "en"; }
export function sslNumber(value: number): string { return new Intl.NumberFormat(sslLocale()).format(value); }
export function sslDays(value: number): string { return new Intl.NumberFormat(sslLocale(), { style: "unit", unit: "day", unitDisplay: "short" }).format(value); }
/** Only source-owned identities enter this lookup; external labels and diagnostics remain literal. */
export function sslText(key: SslKey, values?: SslValues): string {
  const engine = getI18n();
  const replace = values && Object.fromEntries(Object.entries(values).map(([name, value]) => [name, typeof value === "number" ? sslNumber(value) : value]));
  if (!engine?.isInitialized) return source[key].replace(/{{\s*(\w+)\s*}}/g, (_match, name: string) => String(replace?.[name] ?? ""));
  return (engine as unknown as { t: (key: SslKey, options: Record<string, unknown>) => string }).t(key, { ns: "sslManagement", keySeparator: false, replace, interpolation: { escapeValue: false, skipOnVariables: true } });
}
export const sslLocal = (key: SslKey, values?: SslValues): { key: SslKey; values?: SslValues } => ({ key, values });
export const sslMessage = <T extends SslFeedback | null | undefined>(value: T): T extends null | undefined ? T : string => {
  if (typeof value !== "object" || value === null) return value as T extends null | undefined ? T : string;
  if (isSharedRemoteFeedback(value)) return sharedFeedbackText(value) as T extends null | undefined ? T : string;
  let values = value.values;
  if ((value.key === "backendBusy" || value.key === "backendJobFailed" || value.key === "backendJobFailedDetail") && values) {
    const actions = { issue: "backendActionIssue", renew: "backendActionRenew", remove: "backendActionRemove" } as const;
    const action = values.action;
    if (typeof action === "string" && Object.prototype.hasOwnProperty.call(actions, action)) values = { ...values, action: sslText(actions[action as keyof typeof actions]) };
  }
  return sslText(value.key, values) as T extends null | undefined ? T : string;
};
export function sslNodes(key: SslKey, values: Record<string, ReactNode>): ReactNode[] {
  const nodes = Object.values(values);
  const markers = Object.fromEntries(Object.keys(values).map((name, index) => [name, `\uFFF0${index}\uFFF1`]));
  return sslText(key, markers).split(/(\uFFF0\d+\uFFF1)/g).map(part => { const match = /^\uFFF0(\d+)\uFFF1$/.exec(part); return match ? nodes[Number(match[1])] : part; });
}

/** Missing, unknown or malformed provenance leaves the supplied diagnostic untouched. */
export function sslRemote(presentation: unknown, fallback: SslFeedback): SslFeedback {
  const shared = sharedFeedbackValue(typeof fallback === "string" ? fallback : sslMessage(fallback), presentation);
  if (shared) return shared;
  if (!presentation || typeof presentation !== "object" || !Object.prototype.hasOwnProperty.call(presentation, "code")) return fallback;
  const descriptor = presentation as { code?: unknown; params?: unknown };
  if (typeof descriptor.code !== "string" || !descriptor.code.startsWith("ssl.backend")) return fallback;
  const key = descriptor.code.slice(4) as SslKey;
  if (!Object.prototype.hasOwnProperty.call(source, key)) return fallback;
  const params = descriptor.params;
  const values: SslValues = {};
  for (const match of source[key].matchAll(/{{\s*(\w+)\s*}}/g)) {
    const name = match[1]!;
    if (!params || typeof params !== "object" || !Object.prototype.hasOwnProperty.call(params, name)) return fallback;
    const value: unknown = (params as Record<string, unknown>)[name];
    if (typeof value !== "string" && !(typeof value === "number" && Number.isFinite(value))) return fallback;
    values[name] = value;
  }
  return { key, values };
}
export function sslResponseFeedback(response: unknown, fallback: SslFeedback): SslFeedback {
  if (!response || typeof response !== "object") return fallback;
  const data = response as { message?: unknown; presentation?: unknown; error?: unknown };
  if (typeof data.message === "string") return sslRemote(data.presentation, data.message);
  if (data.error && typeof data.error === "object") {
    const nested = data.error as { message?: unknown; presentation?: unknown };
    if (typeof nested.message === "string") return sslRemote(nested.presentation, nested.message);
  }
  return sslRemote(data.presentation, fallback);
}
